import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Outlet, useOutlet, useOutletContext } from 'react-router-dom';
import { FormikHelpers, useFormik } from 'formik';
import { RowSelectionType, RowSelectMethod } from 'antd/es/table/interface';

import { useGetUsersListByNameQuery } from '@Store/api/userApiSlice';
import {
  ApprovalHistory,
  RequestListSearchResponse,
} from '@Types/api/requestList';
import { NotificationContext } from '@Context/NotificationContext';
import {
  PresentationContainer,
  PresentationRequestHeader,
} from '@Components/index';
import { ApprovalsRequestList } from '@Components/RequestList';
import { PAGE_SIZE, TABLE_PAGE_DEFAULT } from '@Features/constants';
import {
  useAppDispatch,
  useAppSelector,
  useFindRequestType,
  useIsLoadedConent,
  useRequestRefetcher,
} from '@Hooks/index';
import { currentUserSelector } from '@Store/selectors/userInfoSelector';
import { SortConfig } from '@Features/types/SortConfig';
import {
  getCompaniesDictSelector,
  getRequestTypeDictSelector,
} from '@Store/selectors/dictionarySelector';
import { FiltersWidget } from '@Components/FiltersWidget';
import { prepareSearchCriterias } from '@Features/utils/prepareSearchCriterias';
import { OrQuery, SearchCriteria } from '@Types/api/common/filters';
import { parseSelectedFiltersToOptions } from '@Features/utils/parseSelectedFiltersToOptions';
import { isFiltersEmptySelector } from '@Store/selectors/filterOptionsSelector';
import { filterOptionsActions } from '@Store/slices/filterOptionsSlice';
import { getOrQueries } from '@Utils/getOrQueries';
import { ApprovalHistory as ApprovalHistoryModal } from '@Components/ApprovalHistory';

import {
  FormApproveRejectRequest,
  FormBatchApproveRejectRequest,
} from '@Types/api/form';
import {
  useBatchApproveMutation,
  useGetTasksCountMutation,
} from '@Store/api/approvalsListApiSlice';
import { useGetApprovalHistoryMutation } from '@Store/api/basicHrmApiSlice';
import { headerActions } from '@Store/slices/headerSlice';
import { getApprovalReqTypeOptions } from '@Store/selectors/approvalsListSelector';
import { ApprovalsTasksResponse } from '@Types/api/approvalsList';
import { ItRequisitionTypeCodes } from '@Enum/api/ItRequisitionTypeCodes.enum';

import { deciderOfApprovePathName } from '@Utils/deciderOfServicePathName';
import { eventMessageActions } from '@Store/slices/eventMessageSlice';
import { MSkeleton } from '@Components/Base';
import { AnyObject } from 'antd/es/_util/type';
import { ColumnDataType } from './list/type';
import { mapColumns } from './list/MapColumns';
import { dataSourceMap } from './list/DataSource';
import ColumnHeader from './components/ColumnHeader';
import Filters from './search/Filter';
import { FilterForm } from './search/types';
import {
  filterTypes,
  getFiltersMapper,
  initialValuesFilterForm,
} from './search/filters.utils';
import { DEFAULT_REQUEST_TYPE_LABEL, orQueriesFields } from './constants';
import { groupedBulkItems } from './utils/groupedBulkItems';

import styles from './task-list.module.css';
import { useGetItApprovalHistoryMutation } from '../requests/ITRequisition/store/api/itRequisitionApiSlice';
import { isSwitchToAllGroupBy } from './utils/isSwitchToAllGroupBy';

type FiltersHandler = (
  values: FilterForm,
  formikHelpers: FormikHelpers<FilterForm>
) => void;

const TaskList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { errorNotification } = useContext(NotificationContext);
  const { isRequestFormPage } = useOutletContext<{
    isRequestFormPage: boolean;
  }>();

  const isRequestFormPagePrev = useRef(isRequestFormPage);

  const [tagsValues, setTagsValues] = useState(initialValuesFilterForm);
  const [showTags, setShowTags] = useState(false);
  const [requestTypeLabel, setRequestTypeLabel] = useState(
    DEFAULT_REQUEST_TYPE_LABEL
  );
  const [createdBefore, setCreatedBefore] = useState<string | undefined>(
    undefined
  );
  const [createdAfter, setCreatedAfter] = useState<string | undefined>(
    undefined
  );
  const [filters, setFilters] = useState<SearchCriteria[] | undefined>(
    undefined
  );
  const [sorting, setSorting] = useState<SortConfig | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<
    React.Key[] | undefined
  >(undefined);
  const [selectedTasksRow, setSelectedTasksRow] = useState<
    ApprovalsTasksResponse[]
  >([]);
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [showFiltersError, setShowFiltersError] = useState(false);
  const [orQueries, setOrQueries] = useState<OrQuery[] | undefined>(undefined);
  const [getApprovalHistory] = useGetApprovalHistoryMutation();
  const [getItApprovalHistory] = useGetItApprovalHistoryMutation();
  const [isApprovalHistoryOpen, setIsApprovalHistoryOpen] = useState(false);
  const [approvalHistoryData, setApprovalHistoryData] = useState<
    ApprovalHistory[] | null
  >(null);
  const { data: users = [] } = useGetUsersListByNameQuery(null);

  const requestTypeOptions = useAppSelector(getApprovalReqTypeOptions);
  const requestTypes = useAppSelector(getRequestTypeDictSelector);
  const isFiltersEmpty = useAppSelector(isFiltersEmptySelector);
  const currentUser = useAppSelector(currentUserSelector);
  const companies = useAppSelector(getCompaniesDictSelector);
  const { findRequestTypeBy } = useFindRequestType();

  const closeFilters = (): void => {
    setIsFiltersOpen(false);
  };

  const openFilters = (): void => {
    setIsFiltersOpen(true);
  };

  const handleApplyFilters: FiltersHandler = (options): void => {
    const filtersMapper = getFiltersMapper(companies, requestTypes);

    const searchCriterias = prepareSearchCriterias(
      options,
      filterTypes,
      filtersMapper
    );

    setFilters(searchCriterias);

    const preparedOrQueries = getOrQueries(
      options,
      orQueriesFields,
      filtersMapper
    );
    setOrQueries(preparedOrQueries);

    setCreatedBefore(options.createdDateTo || undefined);
    setCreatedAfter(options.createdDateFrom || undefined);

    const parsedOptions = parseSelectedFiltersToOptions(options);

    // true if filters was empty and empty after submit
    const isFiltersStillEmpty = isFiltersEmpty && !parsedOptions.length;
    setShowFiltersError(isFiltersStillEmpty);

    const isAllGroupBy = isSwitchToAllGroupBy(
      options.requestTypes,
      requestTypeLabel
    );

    if ((!searchCriterias && !parsedOptions.length) || isAllGroupBy) {
      setRequestTypeLabel('All');
    }

    if (!isFiltersStillEmpty) {
      closeFilters();
    }

    dispatch(filterOptionsActions.setFilterSettings(searchCriterias));
    dispatch(filterOptionsActions.setFilterOptions(parsedOptions));
    dispatch(eventMessageActions.sendEventType('FILTER'));

    setShowTags(true);
    setTagsValues(options);
  };

  const formik = useFormik({
    initialValues: initialValuesFilterForm,
    enableReinitialize: true,
    onSubmit: handleApplyFilters,
  });

  const clearParams = (): void => {
    setSorting(null);
    setFilters(undefined);
    setSearchValue(undefined);
    setTagsValues(initialValuesFilterForm);
    formik.setValues(initialValuesFilterForm);
  };

  const {
    currentPage,
    fetchRequest,
    handleLoadMore,
    onChangeCurrentPage,
    isSuccess,
    listRequests,
    requestsDataList,
  } = useRequestRefetcher({
    clearParams,
    filters,
    isRequestFormPage,
    isRequestFormPagePrev,
    requestType: 'ESSTaskRequest',
    searchValue,
    sorting,
  });

  const isContentLoaded = useIsLoadedConent(isSuccess);

  const [batchApprove] = useBatchApproveMutation();
  const [getTasksCount] = useGetTasksCountMutation();

  const isOutlet = useOutlet();

  useEffect(() => {
    isRequestFormPagePrev.current = isRequestFormPage;
  }, [isRequestFormPage]);

  useEffect(() => {
    dispatch(filterOptionsActions.setFilterOptions(null));
  }, []);

  const dataSource = useMemo(() => {
    if (listRequests?.content) {
      return dataSourceMap(
        listRequests?.content as ApprovalsTasksResponse[],
        findRequestTypeBy
      );
    }

    return [];
  }, [listRequests?.content]);

  const handleChangePage = useCallback((selectedPage: number): void => {
    onChangeCurrentPage(selectedPage);
    dispatch(eventMessageActions.sendEventType('REFETCH'));
  }, []);

  const handleSort = useCallback((sortConfig: SortConfig | null) => {
    setSorting(sortConfig);
    onChangeCurrentPage(TABLE_PAGE_DEFAULT);
  }, []);

  useEffect(() => {
    isRequestFormPagePrev.current = isRequestFormPage;
  }, [isRequestFormPage]);

  const handleOnChange = useCallback(
    (label: string, value: string | null): void => {
      setRequestTypeLabel(label);
      onChangeCurrentPage(TABLE_PAGE_DEFAULT);
      if (label === 'All') {
        setFilters([
          {
            name: 'service',
            value: ['ESS', 'IT', 'RESIGNITION'],
            operator: 'in',
          },
        ]);
        formik.setValues({
          ...initialValuesFilterForm,
          requestTypes: [],
        });
        setShowTags(false);
        return;
      }
      if (value) {
        formik.setValues({
          ...initialValuesFilterForm,
          requestTypes: [label],
        });
        setTagsValues({
          ...initialValuesFilterForm,
          requestTypes: [label],
        });
        setShowTags(true);
        dispatch(filterOptionsActions.setFilterOptions([label]));

        setFilters([
          {
            name: 'requestTypeId',
            value: Number(value),
            operator: 'eq',
          },
        ]);
      }
    },
    [formik.values]
  );

  const isFiltersApplied = Boolean(
    filters || orQueries || createdBefore || createdAfter
  );

  const handleApprovalHistory = useCallback(
    (requestNo: string, isIt?: boolean) => {
      if (isIt) {
        getItApprovalHistory(requestNo)
          .unwrap()
          .then((approvalHistoryResp) => {
            if (
              Array.isArray(approvalHistoryResp) &&
              approvalHistoryResp.length
            ) {
              setIsApprovalHistoryOpen(true);
              setApprovalHistoryData(approvalHistoryResp);
            }
          })
          .catch(({ data: errData }) => {
            errorNotification(errData);
          });
      } else {
        getApprovalHistory(requestNo)
          .unwrap()
          .then((approvalHistoryResp) => {
            if (
              Array.isArray(approvalHistoryResp) &&
              approvalHistoryResp.length
            ) {
              setIsApprovalHistoryOpen(true);
              setApprovalHistoryData(approvalHistoryResp);
            }
          })
          .catch(({ data: error }) => {
            errorNotification(error);
          });
      }
    },
    [errorNotification, getApprovalHistory, getItApprovalHistory]
  );

  const isSelected = Boolean(selectedRowKeys?.length);

  const selectedAllRows = useMemo(() => {
    if (dataSource?.length) {
      return selectedRowKeys?.length === dataSource?.length;
    }

    return false;
  }, [selectedRowKeys?.length, dataSource?.length]);

  const renderColumnTitle = useCallback(
    (originalNode: React.ReactNode) => (
      <div className={`${selectedAllRows ? 'trojan-selected-all' : ''}`}>
        {originalNode}
      </div>
    ),
    [selectedAllRows]
  );

  const isEmailCreation = ({ reqItemId }: ApprovalsTasksResponse): boolean => {
    if (reqItemId === ItRequisitionTypeCodes.EMAIL) {
      return true;
    }
    return false;
  };

  const isSimCard = ({ reqItemId }: ApprovalsTasksResponse): boolean => {
    if (reqItemId === ItRequisitionTypeCodes.NEW_SIM_CARD) {
      return true;
    }
    return false;
  };

  const isBSATask = ({ reqItemId }: ApprovalsTasksResponse): boolean => {
    if (reqItemId === ItRequisitionTypeCodes.BUILD_SMART_ACCESS) {
      return true;
    }
    return false;
  };

  const isSimAddonTask = ({ reqItemId }: ApprovalsTasksResponse): boolean => {
    if (reqItemId === ItRequisitionTypeCodes.SIM_ADDON_REQUEST) {
      return true;
    }
    return false;
  };

  const isPassportTask = ({ taskType }: ApprovalsTasksResponse): boolean => {
    return taskType === 'Passport Request';
  };

  const rowSelection = useMemo(() => {
    return {
      onChange: (
        selectedKeys: React.Key[],
        selectedRows: Array<AnyObject>,
        info: {
          type: RowSelectMethod;
        }
      ) => {
        const tasksData =
          info.type === 'none'
            ? (selectedRows as ApprovalsTasksResponse[])
            : (selectedRows as ColumnDataType[]).map((row) => row.taskData);

        const mappedSelectedTasks = tasksData
          ?.filter(
            (item) =>
              !isBSATask(item) &&
              !isSimAddonTask(item) &&
              !isSimCard(item) &&
              !isEmailCreation(item) &&
              !isPassportTask(item)
          )
          .map((item) => item);

        const filtredSelectedKeys = mappedSelectedTasks.map(
          (task) => task.taskId as React.Key
        );

        setSelectedTasksRow(mappedSelectedTasks);
        setSelectedRowKeys(filtredSelectedKeys);
      },
      fixed: true,
      columnTitle: renderColumnTitle,
      selectedRowKeys,
      type: 'checkbox' as RowSelectionType,
    };
  }, [renderColumnTitle, selectedRowKeys]);

  const handleBulkApprove = useCallback(() => {
    const batchItems = groupedBulkItems(selectedTasksRow, findRequestTypeBy);

    for (const batchRequestType in batchItems) {
      if (Object.hasOwn(batchItems, batchRequestType)) {
        const servicePathname = deciderOfApprovePathName(batchRequestType);
        const body = {
          commands: batchItems[batchRequestType]?.map((batchItemId) => ({
            taskId: batchItemId,
            comment: '',
            command: 'Approve',
          })) as FormApproveRejectRequest[],
          command: 'Approve',
        } as FormBatchApproveRejectRequest;

        batchApprove({ servicePathname, body })
          .unwrap()
          .then(() => {
            setSelectedTasksRow([]);
            setSelectedRowKeys(undefined);
            fetchRequest();
          })
          .catch(() => {});
      }
    }
  }, [selectedTasksRow, fetchRequest]);

  const handleBulkReject = useCallback(() => {
    const batchItems = groupedBulkItems(selectedTasksRow, findRequestTypeBy);

    for (const batchRequestType in batchItems) {
      if (Object.hasOwn(batchItems, batchRequestType)) {
        const servicePathname = deciderOfApprovePathName(batchRequestType);
        const body = {
          commands: batchItems[batchRequestType]?.map((batchItemId) => ({
            taskId: batchItemId,
            comment: '',
            command: 'Reject',
          })) as FormApproveRejectRequest[],
          command: 'Reject',
        } as FormBatchApproveRejectRequest;

        batchApprove({ servicePathname, body })
          .unwrap()
          .then(() => {
            setSelectedTasksRow([]);
            setSelectedRowKeys(undefined);
            fetchRequest();
          })
          .catch(() => {});
      }
    }
  }, [selectedTasksRow, fetchRequest]);

  const renderColumnHeader = useCallback(
    (): JSX.Element => (
      <ColumnHeader
        isAllSelected={selectedAllRows}
        count={selectedRowKeys?.length || 0}
        onApprove={handleBulkApprove}
        onReject={handleBulkReject}
      />
    ),
    [handleBulkApprove, handleBulkReject, selectedAllRows, selectedRowKeys]
  );

  const handleSearch = useCallback((value: string | null) => {
    setSearchValue(value || undefined);
  }, []);

  const fetchTasksCount = useCallback(() => {
    getTasksCount()
      .unwrap()
      .then((response) => {
        dispatch(headerActions.setTasksCount(response));
      });
  }, [currentUser]);

  const tagsFormik = {
    ...formik,
    values: tagsValues,
  };

  return (
    <div className={styles.wrapper}>
      {isOutlet ? (
        <Outlet />
      ) : (
        <>
          <PresentationRequestHeader
            caption="Approvals"
            labelSelectOption="Group By"
            selectOptions={requestTypeOptions}
            selectedValue={requestTypeLabel}
            handleSelect={handleOnChange}
            onRequestSelectorOpen={fetchTasksCount}
          />
          <PresentationContainer className={styles.taskList}>
            <FiltersWidget
              onSearchSubmit={handleSearch}
              isOpen={isFiltersOpen}
              onClose={closeFilters}
              onOpen={openFilters}
              formik={tagsFormik}
              showTags={showTags}
            >
              <Filters formik={formik} showError={showFiltersError} />
            </FiltersWidget>
            {isContentLoaded ? (
              <ApprovalsRequestList
                onLoadMore={handleLoadMore}
                listRequests={
                  listRequests as RequestListSearchResponse<ApprovalsTasksResponse>
                }
                requestsDataList={
                  requestsDataList as Array<ApprovalsTasksResponse>
                }
                selectedTasksRow={selectedTasksRow}
                requestType="ESSTaskRequest"
                dataSource={dataSource}
                mapColumns={mapColumns}
                totalItems={listRequests?.totalElements}
                pageSize={PAGE_SIZE}
                onApproveReject={fetchRequest}
                onApprovalHistory={handleApprovalHistory}
                currentPage={currentPage}
                onChangePage={handleChangePage}
                onSort={handleSort}
                onBulkApprove={handleBulkApprove}
                onBulkReject={handleBulkReject}
                rowSelection={rowSelection}
                title={isSelected ? renderColumnHeader : undefined}
                isFiltersApplied={isFiltersApplied}
                isSearchApplied={Boolean(searchValue)}
              />
            ) : (
              <MSkeleton />
            )}
          </PresentationContainer>

          <ApprovalHistoryModal
            isOpen={isApprovalHistoryOpen}
            onClose={() => {
              setIsApprovalHistoryOpen(false);
            }}
            data={approvalHistoryData}
            users={users}
          />
        </>
      )}
    </div>
  );
};

export default TaskList;
